import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Input,
  Button,
} from "reactstrap"

import { useNavigate, useParams } from "react-router-dom"
import * as Yup from "yup"

import { interestFrequency, repaymentSchedule } from "helpers/utils"
import {
  formatAmount,
  getDateOfRepayment,
  getFundRequirementDate,
  getIcdRequestStatus,
} from "helpers/common"
import {
  useApproveOfferInvestorMutation,
  useIcdPaymentMutation,
  useLazyViewMyIcdRequestsQuery,
  useRejectOfferInvestorMutation,
} from "store/api/investorapi/icds.api"
import toast from "react-hot-toast"
import LoaderComponent from "components/loader"
import RejectConfirmationModal from "./confirm/rejectConfirmation"
import ApproveConfirmationModal from "./confirm/approveConfirmation"
import ICDAgreementBox from "pages/Dashboard/icdAgreementBox"
import Spinners from "components/Common/Spinner"
import PaymentUtrModel from "modals/paymentUTRModel"
import { useFormik } from "formik"
// import IcdPaymentBox from "./icdPaymentBox"
import { load } from "@cashfreepayments/cashfree-js"
import { usePermission } from "hooks/usePermission"
import SignAgreementModal from "../signAgreementModal"

const MyICDRequestsView = () => {
  //meta title
  document.title = "ICD Request | Prachay"

  const { id } = useParams()
  const navigate = useNavigate()
  const [icdData, setIcdData] = useState(null)

  const [getDetails, { isFetching }] = useLazyViewMyIcdRequestsQuery()
  const [mutate, { isLoading }] = useIcdPaymentMutation()

  const [isToast, setIsToast] = useState(false)
  const [actionType, setActionType] = useState() //APPROVE,REJECT

  const [onApprove, { isLoading: isApproving }] =
    useApproveOfferInvestorMutation()
  const [onReject, { isLoading: isRejecting }] =
    useRejectOfferInvestorMutation()

  const [open, setOpen] = useState(false)
  const [isPayment, setIsPayment] = useState(false)
  const [cashfree, setCashfree] = useState(null)
  const [signingRequestOpen, setSigningRequestOpen] = useState(false)
  const { hasPermission } = usePermission()

  const onGetDetails = async () => {
    try {
      const { data } = await getDetails({ id }).unwrap()
      setIcdData(data)
    } catch (error) {
      if (error.status === 404) {
        navigate("/icd-investments/my-icds")
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    if (icdData && icdData?.icdsRequest?.status === "TERM_SHEET_SHARED")
      setOpen(true)
  }, [icdData])

  const handlePayment = async (values) => {
    try {
      const { data, success, message } = await mutate({
        icdId: icdData?.icdsRequest.id,
        ...values,
        returnUrl: window.location.href,
      }).unwrap()
      if (success) {
        if (values.paymentType === "ONLINE") {
          createOrder(data)
        } else if (values.paymentType === "OFFLINE") {
          toast.success(message, { id: "toastMessage" })
          window.location.reload()
        }
      }
    } catch (error) {
      if (values.paymentType === "OFFLINE") {
        const data = error?.data
        if (data?.errors) {
          const { errors } = data
          paymentValidation.setErrors(errors)
        }
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    const initializeSDK = async () => {
      try {
        const cf = await load({
          mode: "production", //sandbox ,production
        })
        setCashfree(cf)
      } catch (error) {
        console.error("Failed to initialize Cashfree SDK", error)
      }
    }

    initializeSDK()
  }, [])

  const createOrder = async (data) => {
    if (!cashfree) {
      console.error("Cashfree SDK not initialized yet")
      return
    }

    const checkoutOptions = {
      paymentSessionId: data.payment_session_id,
      redirectTarget: "_modal",
    }

    cashfree.checkout(checkoutOptions).then((result) => {
      if (result.error) {
        console.log(
          "User closed the popup or payment error occurred:",
          result.error
        )
      }

      if (result.redirect) {
        console.log("Payment redirect required, will be handled automatically")
      }

      if (result.paymentDetails) {
        window.location.reload()
        console.log("Payment completed, check status on backend")
        console.log("Message:", result.paymentDetails.paymentMessage)
      }
    })
  }

  const paymentValidation = useFormik({
    enableReinitialize: true,
    initialValues: { itr: "", amount: "" },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await handlePayment({ ...values, paymentType: "OFFLINE" })
    },
  })

  useEffect(() => {
    if (id) {
      onGetDetails()
    }
  }, [id])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {isFetching ? (
            <LoaderComponent />
          ) : (
            <Row className="mt-4">
              <Col xl={12}>
                <Card>
                  <CardBody className="pt-4">
                    <div className="d-flex align-items-center mb-4 justify-content-between ">
                      <CardTitle className="heading-badge">
                        <span> ICD Request ( {icdData?.unique_code} )</span>
                      </CardTitle>
                      {!isFetching && icdData && (
                        <>
                          {(() => {
                            const icdsReq = icdData?.icdsRequest
                            return (
                              <div className="code-status">
                                {getIcdRequestStatus(icdsReq.status)}
                              </div>
                            )
                          })()}
                        </>
                      )}
                    </div>
                    <Row className="mb-2">
                      <Col xl={12}>
                        <Row className="mb-2">
                          <Col xl={12}>
                            <Row className="mb-2">
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Investment Amount
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-firstname-Input"
                                  placeholder="Investment Amount"
                                  value={`₹ ${formatAmount(icdData?.icdsRequest?.countered ? icdData?.amount : icdData?.icdsRequest?.amount)}`}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Date of Repayment
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Date of Repayment"
                                  value={getDateOfRepayment(
                                    icdData?.repayment_date
                                  )}
                                />
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Tenure(in days)
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Tenure"
                                  value={icdData?.tenure}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Interest Rate (%)
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Interest Rate"
                                  value={icdData?.interest_rate}
                                />
                              </Col>

                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Interest Payment Frequency
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Interest Rate"
                                  value={
                                    icdData &&
                                    interestFrequency.find(
                                      (item) =>
                                        item.key === icdData?.interest_frequency
                                    ).label
                                  }
                                />
                              </Col>

                              <Col xl={6}>
                                <Label className="col-form-label">
                                  {icdData?.created_by === "ADMIN"
                                    ? "Fund Requirement Date"
                                    : "Fund Availability Date"}
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Fund Availability Date"
                                  value={getFundRequirementDate(
                                    icdData?.fund_requirement_date
                                  )}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Repayment Schedule
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Fund Requirement Date"
                                  value={
                                    icdData &&
                                    repaymentSchedule.find(
                                      (item) =>
                                        item.key === icdData?.repayment_schedule
                                    ).label
                                  }
                                />
                              </Col>
                              {icdData?.repayment_schedule === "MULTIPLE" && (
                                <Col xl={6}>
                                  <Label className="col-form-label">
                                    No. Of Installments
                                  </Label>
                                  <Input
                                    name="repayment_terms"
                                    type="number"
                                    disabled
                                    className="form-control"
                                    id="repayment_terms"
                                    placeholder="Total Installments"
                                    value={icdData.repayment_terms}
                                  />
                                </Col>
                              )}
                            </Row>
                          </Col>
                        </Row>

                        {icdData?.icdsRequest?.countered && (
                          <>
                            <Row className="mb-2">
                              <CardTitle className="my-4">
                                Counter Offer
                              </CardTitle>
                            </Row>

                            <Row className="mb-2">
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Investment Amount
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-firstname-Input"
                                  placeholder="Investment Amount"
                                  value={`₹ ${formatAmount(icdData?.icdsRequest?.amount)}`}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Date of Repayment
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Date of Repayment"
                                  value={getDateOfRepayment(
                                    icdData?.icdsRequest?.repayment_date
                                  )}
                                />
                              </Col>
                            </Row>
                            <Row className="mb-2">
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Tenure(in days)
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Tenure"
                                  value={icdData?.icdsRequest?.tenure}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Interest Rate (%)
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Interest Rate"
                                  value={icdData?.icdsRequest?.interest_rate}
                                />
                              </Col>

                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Interest Payment Frequency
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  placeholder="Interest Rate"
                                  value={
                                    icdData &&
                                    interestFrequency.find(
                                      (item) =>
                                        item.key ===
                                        icdData?.icdsRequest?.interest_frequency
                                    ).label
                                  }
                                />
                              </Col>

                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Fund Availability Date
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Fund Availability Date"
                                  value={
                                    icdData &&
                                    getFundRequirementDate(
                                      icdData.fund_requirement_date
                                    )
                                  }
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Repayment Schedule
                                </Label>
                                <Input
                                  disabled
                                  type="text"
                                  className="form-control"
                                  id="horizontal-email-id "
                                  placeholder="Fund Requirement Date"
                                  value={
                                    icdData &&
                                    repaymentSchedule.find(
                                      (item) =>
                                        item.key ===
                                        icdData?.icdsRequest?.repayment_schedule
                                    ).label
                                  }
                                />
                              </Col>
                              {icdData?.icdsRequest?.repayment_schedule ===
                                "MULTIPLE" && (
                                <Col xl={6}>
                                  <Label className="col-form-label">
                                    No. Of Installments
                                  </Label>
                                  <Input
                                    name="repayment_terms"
                                    type="number"
                                    disabled
                                    className="form-control"
                                    id="repayment_terms"
                                    placeholder="Total Installments"
                                    value={
                                      icdData?.icdsRequest?.repayment_terms
                                    }
                                  />
                                </Col>
                              )}

                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Lock-in (in days)
                                </Label>
                                <Input
                                  name="lockin_period"
                                  type="number"
                                  disabled
                                  className="form-control"
                                  id="lockin_period"
                                  placeholder="Days"
                                  value={icdData?.icdsRequest?.lockin_period}
                                />
                              </Col>
                              <Col xl={6}>
                                <Label className="col-form-label">
                                  Notice Period (in days)
                                </Label>
                                <Input
                                  name="lockin_period"
                                  type="number"
                                  disabled
                                  className="form-control"
                                  id="lockin_period"
                                  placeholder="Days"
                                  value={icdData?.icdsRequest?.notice_period}
                                />
                              </Col>
                            </Row>
                            {icdData?.icdsRequest?.comments && (
                              <Row className="mb-2">
                                <Col xl={12}>
                                  <Label
                                    htmlFor="horizontal-firstname-Input"
                                    className="col-form-label"
                                  >
                                    Comments / Notes
                                  </Label>
                                  <Input
                                    disabled
                                    name="Name "
                                    type="textarea"
                                    className="form-control textarea"
                                    id="horizontal-email-id "
                                    placeholder="Comment"
                                    value={icdData?.icdsRequest?.comments}
                                  />
                                </Col>
                              </Row>
                            )}
                          </>
                        )}
                      </Col>
                    </Row>
                    {icdData?.icdsRequest?.status ===
                      "TERM_SHEET_FINALISED" && (
                      <>
                        <Row className="justify-content-end mt-4">
                          <Col sm={12}>
                            <div className="d-flex justify-content-end align-items-center gap-3">
                              {hasPermission("OFFLINE_PAYMENT_INVESTMENT") && (
                                <button
                                  onClick={() => {
                                    setIsPayment(true)
                                    paymentValidation.setFieldValue(
                                      "amount",
                                      icdData?.icdsRequest?.amount -
                                        (icdData?.icdsRequest?.investorsPayments?.reduce(
                                          (sum, item) =>
                                            sum + (item.amount || 0),
                                          0
                                        ) || 0)
                                    )
                                  }}
                                  type="button"
                                  className="btn btn-cancel w-md"
                                >
                                  Offline Payment
                                </button>
                              )}
                              {hasPermission("ONLINE_PAYMENT_INVESTMENT") &&
                                icdData?.icdsRequest?.investorsPayments
                                  .length <= 0 && (
                                  <Button
                                    onClick={() =>
                                      handlePayment({ paymentType: "ONLINE" })
                                    }
                                    disabled={!isPayment && isLoading}
                                    className="btn btn-primary w-md"
                                  >
                                    {!isPayment && isLoading ? (
                                      <Spinners />
                                    ) : (
                                      "Online Payment"
                                    )}
                                  </Button>
                                )}
                            </div>
                          </Col>
                        </Row>
                        <PaymentUtrModel
                          icd={icdData?.icdsRequest}
                          open={isPayment}
                          setOpen={setIsPayment}
                          validation={paymentValidation}
                          isLoading={isLoading}
                        />
                      </>
                    )}
                    {icdData?.icdsRequest?.created_by === "ADMIN" &&
                      icdData?.icdsRequest?.status === "COUNTER_OFFER" && (
                        <>
                          <Row>
                            <Col xl={12}>
                              <div className="buttons">
                                {hasPermission("REJECT_INVESTMENT_REQUEST") && (
                                  <Button
                                    className="reject"
                                    onClick={() => {
                                      setIsToast(true)
                                      setActionType("REJECT")
                                    }}
                                  >
                                    Reject
                                  </Button>
                                )}
                                {hasPermission("ACCEPT_INVESTMENT_REQUEST") && (
                                  <Button
                                    className="approve"
                                    onClick={() => {
                                      setIsToast(true)
                                      setActionType("APPROVE")
                                    }}
                                  >
                                    Approve
                                  </Button>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    <ICDAgreementBox
                      open={open}
                      setOpen={setOpen}
                      template_type="TERM_SHEET"
                      icd_id={icdData?.icdsRequest?.id}
                    />
                    {icdData && isToast && actionType === "APPROVE" && (
                      <ApproveConfirmationModal
                        open={isToast}
                        id={icdData.id}
                        icdData={icdData}
                        setOpen={setIsToast}
                        isLoading={isApproving}
                        onClose={() => {
                          setIsToast(false)
                          setActionType("")
                        }}
                        onApprove={onApprove}
                        onSuccess={() => {
                          onGetDetails()
                          setIsToast(false)
                        }}
                        setSigningRequestOpen={setSigningRequestOpen}
                      />
                    )}
                    {icdData && isToast && actionType === "REJECT" && (
                      <RejectConfirmationModal
                        open={isToast}
                        id={icdData.id}
                        setOpen={setIsToast}
                        isLoading={isRejecting}
                        onClose={() => {
                          setIsToast(false)
                          setActionType("")
                        }}
                        onReject={onReject}
                        onSuccess={() => {
                          onGetDetails()
                          setIsToast(false)
                        }}
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      <SignAgreementModal
        open={signingRequestOpen}
        setOpen={setSigningRequestOpen}
        showAlert={true}
      />
    </React.Fragment>
  )
}

export default MyICDRequestsView
